import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";
import { Link } from "react-router-dom";
import {
  Phone,
  Mail,
  Clock,
  MapPin,
  Shield,
  CheckCircle,
  Truck,
  Building,
  Home,
  Factory,
  Navigation,
  Wrench,
  Calendar,
  Star,
} from "lucide-react";
import asphaltPavingDriveway from "@/assets/asphalt-paving-driveway.jpg";
import commercialParkingLotPaving from "@/assets/commercial-parking-lot-paving.jpg";
import concreteOntario from "@/assets/concrete-ontario.jpg";
import roadResurfacingRepair from "@/assets/road-resurfacing-repair.jpg";

const AsphaltPavingConcreteServices = () => {
  const services = [
    {
      title: "Asphalt Paving & Concrete Experts in Niagara, Toronto & Hamilton ON",
      icon: <Navigation className="h-6 w-6" />,
      description:
        "Professional asphalt paving for driveways, parking lots, and roads in Niagara, Toronto, and Hamilton Ontario..",
      features: [
        "New asphalt installation",
        "Hot mix asphalt paving",
        "Residential driveways",
        "Commercial parking lots",
        "Road construction",
        "Surface preparation",
        "Proper drainage systems",
        "Quality compaction",
      ],
      image: asphaltPavingDriveway,
      alt: "Professional asphalt driveway paving in Ontario",
    },
    {
      title: "Concrete Work & Pouring",
      icon: <Building className="h-6 w-6" />,
      description: "Complete concrete services from foundations to decorative finishes for all project types.",
      features: [
        "Concrete foundations",
        "Sidewalks & walkways",
        "Patios & driveways",
        "Commercial slabs",
        "Stamped concrete",
        "Decorative concrete",
        "Concrete repair",
        "Reinforced concrete",
      ],
      image: concreteOntario,
      alt: "Concrete foundation and slab work in Ontario",
    },
    {
      title: "Paving Repair & Maintenance",
      icon: <Wrench className="h-6 w-6" />,
      description: "Expert repair and maintenance services to extend the life of your existing pavement.",
      features: [
        "Crack sealing",
        "Pothole repair",
        "Asphalt resurfacing",
        "Seal coating",
        "Line striping",
        "Drainage repair",
        "Surface restoration",
        "Preventive maintenance",
      ],
      image: roadResurfacingRepair,
      alt: "Road resurfacing and asphalt repair work",
    },
    {
      title: "Commercial Paving Solutions",
      icon: <Factory className="h-6 w-6" />,
      description: "Large-scale paving projects for commercial, industrial, and municipal clients.",
      features: [
        "Parking lot construction",
        "Loading dock areas",
        "Industrial yards",
        "Municipal roadways",
        "Shopping centers",
        "Office complexes",
        "Warehouse facilities",
        "Heavy-duty surfaces",
      ],
      image: commercialParkingLotPaving,
      alt: "Commercial parking lot paving construction",
    },
  ];

  const projectTypes = [
    {
      category: "Residential Paving",
      projects: [
        "Asphalt driveways",
        "Concrete driveways",
        "Walkways & pathways",
        "Patio installation",
        "Pool decks",
        "Retaining walls",
      ],
    },
    {
      category: "Commercial Paving",
      projects: [
        "Parking lot construction",
        "Loading zones",
        "Warehouse floors",
        "Retail store access",
        "Office building entrances",
        "Industrial surfaces",
      ],
    },
    {
      category: "Municipal & Public Works",
      projects: [
        "Road construction",
        "Street resurfacing",
        "Sidewalk installation",
        "Bike path construction",
        "Public parking areas",
        "Municipal building access",
      ],
    },
  ];

  const benefits = [
    {
      icon: <Shield className="h-5 w-5" />,
      title: "Licensed & Insured",
      description: "Fully licensed paving contractors with comprehensive insurance coverage for all projects.",
    },
    {
      icon: <Star className="h-5 w-5" />,
      title: "Quality Materials",
      description: "Premium asphalt mixes and concrete formulations for maximum durability and longevity.",
    },
    {
      icon: <Truck className="h-5 w-5" />,
      title: "Professional Equipment",
      description: "State-of-the-art paving equipment and trucks for efficient, high-quality installations.",
    },
    {
      icon: <Calendar className="h-5 w-5" />,
      title: "Flexible Scheduling",
      description: "Work around your schedule with evening and weekend availability for residential projects.",
    },
  ];

  const faqData = [
    {
      question: "How long does asphalt paving take to cure?",
      answer:
        "Fresh asphalt can typically be walked on within 24-48 hours and driven on within 3-7 days, depending on weather conditions and thickness. Full curing takes 6-12 months.",
    },
    {
      question: "What's the difference between asphalt and concrete driveways?",
      answer:
        "Asphalt is more flexible, costs less initially, and is easier to repair. Concrete lasts longer, requires less maintenance, and offers more decorative options but costs more upfront.",
    },
    {
      question: "How often should I seal coat my asphalt driveway?",
      answer:
        "Seal coating should be applied every 2-3 years for residential driveways and annually for high-traffic commercial areas to protect against weather and extend pavement life.",
    },
    {
      question: "Can you pave in winter or during rain?",
      answer:
        "Asphalt paving requires dry conditions and temperatures above 50°F. We typically pave from April through October in Ontario, avoiding wet or freezing weather.",
    },
    {
      question: "Do you provide warranties on paving work?",
      answer:
        "Yes, we provide comprehensive warranties on all paving work - typically 2-5 years on materials and workmanship, depending on the project type and conditions.",
    },
    {
      question: "How do I prepare my property for paving?",
      answer:
        "Clear the area of vehicles and obstacles, mark underground utilities, and ensure access for our equipment. We'll handle excavation, grading, and base preparation.",
    },
  ];

  const serviceSchema = {
    serviceType: "Asphalt Paving and Concrete Services",
    serviceName: "Professional Paving Solutions Ontario",
    services: services.map((service) => ({
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: service.title,
        description: service.description,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-background">
      <SchemaMarkup type="Service" data={serviceSchema} />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-glow text-primary-foreground py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                Professional Asphalt Paving & Concrete Services in Ontario
              </h1>
              <p className="text-xl opacity-90 mb-8">
                Blue Knight Contracting delivers expert asphalt paving, concrete work, and pavement maintenance services
                across Ontario. From residential driveways to commercial parking lots, we provide durable, high-quality
                paving solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  variant="hero"
                  size="lg"
                  className="bg-primary-foreground text-white hover:bg-primary-foreground/90"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Get Free Paving Quote
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="text-white border-white hover:bg-white hover:text-primary"
                >
                  <Calendar className="mr-2 h-5 w-5" />
                  Schedule Site Visit
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src={asphaltPavingDriveway}
                alt="Professional asphalt driveway paving project in Ontario"
                className="rounded-lg shadow-xl w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">Complete Asphalt & Concrete Solutions</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              From new installations to repairs and maintenance, our experienced team provides comprehensive paving
              services for residential, commercial, and municipal projects.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="overflow-hidden border-2 hover:border-primary/20 transition-colors">
                <div className="aspect-video relative">
                  <img src={service.image} alt={service.alt} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="bg-primary p-2 rounded-lg">{service.icon}</div>
                      <h3 className="text-xl font-bold">{service.title}</h3>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-muted-foreground mb-6">{service.description}</p>
                  <div className="grid md:grid-cols-2 gap-2">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                        {feature}
                      </div>
                    ))}
                  </div>
                  <Button className="w-full mt-6">Get Quote for {service.title}</Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Project Types */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">Paving Projects We Handle</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Our experienced team manages paving projects of all sizes and complexity levels across residential,
              commercial, and municipal sectors.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {projectTypes.map((type, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <div className="bg-primary/10 p-2 rounded-lg">
                      {index === 0 && <Home className="h-5 w-5" />}
                      {index === 1 && <Building className="h-5 w-5" />}
                      {index === 2 && <Navigation className="h-5 w-5" />}
                    </div>
                    {type.category}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {type.projects.map((project, projectIndex) => (
                      <li key={projectIndex} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                        {project}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">Why Choose Blue Knight for Paving Services?</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              With decades of experience and state-of-the-art equipment, we deliver superior paving results that stand
              the test of time.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="text-center border-2 hover:border-primary/20 transition-colors">
                <CardContent className="pt-6">
                  <div className="bg-primary/10 p-3 rounded-lg w-fit mx-auto mb-4">{benefit.icon}</div>
                  <h3 className="font-semibold text-lg mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">Our Paving Process</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              We follow a proven process to ensure every paving project meets the highest standards of quality and
              durability.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: "1",
                title: "Site Assessment",
                description: "Free on-site evaluation, measurements, and detailed project estimate",
              },
              {
                step: "2",
                title: "Preparation",
                description: "Excavation, grading, base installation, and utility marking",
              },
              {
                step: "3",
                title: "Installation",
                description: "Professional paving with quality materials and proper compaction",
              },
              {
                step: "4",
                title: "Finishing",
                description: "Final grading, cleanup, and project walkthrough with warranty",
              },
            ].map((process, index) => (
              <Card key={index} className="text-center">
                <CardContent className="pt-6">
                  <div className="bg-primary text-primary-foreground w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {process.step}
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{process.title}</h3>
                  <p className="text-sm text-muted-foreground">{process.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">Serving Ontario Communities</h2>
              <p className="text-xl text-muted-foreground mb-8">
                Blue Knight Contracting provides professional asphalt paving and concrete services throughout Ontario,
                including major cities and surrounding communities.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {[
                  "Toronto & GTA",
                  "Ottawa Region",
                  "Hamilton Area",
                  "London & Southwestern Ontario",
                  "Kitchener-Waterloo",
                  "Windsor & Essex County",
                ].map((area, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-primary" />
                    <span className="text-sm">{area}</span>
                  </div>
                ))}
              </div>
              <Button variant="cta" size="lg">
                <Phone className="mr-2 h-5 w-5" />
                Call for Service in Your Area
              </Button>
            </div>
            <div className="relative">
              <img
                src={commercialParkingLotPaving}
                alt="Commercial parking lot paving project across Ontario"
                className="rounded-lg shadow-xl w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg"></div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">Frequently Asked Questions</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Get answers to common questions about asphalt paving, concrete work, and pavement maintenance services.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible>
              {faqData.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger>{faq.question}</AccordionTrigger>
                  <AccordionContent>{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">Ready to Start Your Paving Project?</h2>
          <p className="text-xl opacity-90 max-w-3xl mx-auto mb-8">
            Contact Blue Knight Contracting today for a free estimate on your asphalt paving, concrete work, or pavement
            maintenance project. Quality results guaranteed.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button
              variant="hero"
              size="lg"
              className="bg-primary-foreground text-white hover:bg-primary-foreground/90"
            >
              <Phone className="mr-2 h-5 w-5" />
              Call (647) 760-8854
            </Button>
            <Link to="/contact">
              <Button variant="outline" size="lg" className="text-white bg-primary border-white hover:bg-white hover:text-primary">
                <Mail className="mr-2 h-5 w-5" />
                Request Free Quote
              </Button>
            </Link>
          </div>

          <div className="bg-primary-foreground/10 p-6 rounded-lg max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Clock className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Emergency Paving Repairs Available</h3>
            </div>
            <p className="text-sm opacity-90">
              24/7 emergency service for critical pavement repairs and temporary fixes. Call our emergency line for
              immediate assistance.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AsphaltPavingConcreteServices;
